import { CartAddEvent, CartErrorEvent } from '@theme/events';

class CZStickyATC extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.sequence = 0;
    this.confirmedSize = null;
    this.pending = false;
    this.adding = false;
    this.uncertain = false;
    this.syncFailed = false;
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.initialize();
  }

  async initialize() {
    try {
      this.config = JSON.parse(this.querySelector('[data-cz-config]').textContent);
      if (!this.config.productId || !this.config.sectionId) return;
      await Promise.race([
        Promise.all([customElements.whenDefined('product-form-component'), ...(this.config.options.length ? [customElements.whenDefined('variant-picker')] : [])]),
        new Promise((_, reject) => { this.initTimer = setTimeout(() => reject(new Error('Theme unavailable')), 10000); })
      ]);
      clearTimeout(this.initTimer);
      if (!this.isConnected || this.controller.signal.aborted) return;
      this.locateNative();
      if (!this.form || !this.nativeButton || (this.config.options.length && !this.picker)) return;
      if (document.querySelector('cz-sticky-atc[data-ready="true"]')) return;
      this.bar = this.querySelector('[data-cz-bar]');
      this.dialog = this.querySelector('[data-cz-dialog]');
      if (!this.dialog?.showModal) return;
      this.openButton = this.querySelector('[data-cz-open]');
      this.submitButton = this.querySelector('[data-cz-submit]');
      const signal = this.controller.signal;
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cz-size]');
        if (input) this.selectSize(input.value);
        const option = event.target.closest('[data-cz-option]');
        if (option) this.selectOption(Number(option.dataset.position), option.value);
      }, { signal });
      document.addEventListener('change', (event) => this.onNativeChange(event), { capture: true, signal });
      document.addEventListener('variant:selected', (event) => this.onVariantSelected(event), { signal });
      document.addEventListener('variant:update', (event) => this.onVariantUpdate(event), { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.detail?.sectionId === this.config.sectionId) this.cleanup();
      }, { signal });
      this.dialog.addEventListener('cancel', (event) => {
        if (this.adding) event.preventDefault();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('cz-atc-scroll-lock');
        this.updateVisibility();
        const restore = this.returnFocus !== false;
        this.returnFocus = true;
        requestAnimationFrame(() => {
          if (!restore || !this.isConnected || document.querySelector('dialog[open]')) return;
          const target = this.openButton.getClientRects().length ? this.openButton : this.nativeButton;
          target?.focus({ preventScroll: true });
        });
      }, { signal });
      this.mobile.addEventListener('change', () => {
        this.updateOpenLabel();
        this.updateVisibility();
      }, { signal });
      this.intersection = new IntersectionObserver(([entry]) => {
        this.nativeVisible = entry.isIntersecting;
        this.updateVisibility();
      }, { threshold: 0 });
      this.intersection.observe(this.nativeContainer);
      this.reserve = document.createElement('div');
      this.reserve.setAttribute('aria-hidden', 'true');
      this.reserve.dataset.czReserve = this.config.sectionId;
      document.body.append(this.reserve);
      this.resize = new ResizeObserver(() => this.updateReserve());
      this.resize.observe(this.bar);
      this.coverObserver = new MutationObserver(() => {
        if (this.coverFrame) return;
        this.coverFrame = requestAnimationFrame(() => {
          this.coverFrame = null;
          this.updateVisibility();
        });
      });
      this.coverObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'aria-hidden'] });
      this.syncNativeVisibility();
      this.hidden = false;
      this.dataset.ready = 'true';
      this.applyConfig(this.config);
      this.updateVisibility();
    } catch {
      this.cleanup();
    }
  }

  syncNativeVisibility() {
    const replaceMobile = Boolean(this.config.settings.replaceMobile);
    this.nativeContainer?.classList.toggle('cz-atc-native-mobile', replaceMobile);
    this.picker?.querySelectorAll('.cz-atc-size-mobile').forEach((element) => {
      element.classList.remove('cz-atc-size-mobile');
    });
    if (!replaceMobile || !this.config.hasSize || !this.picker) return;
    const control = this.findOptionControl(this.config.sizePosition);
    const group = control?.closest('.variant-option, fieldset');
    if (!group || !this.picker.contains(group)) return;
    const controls = [...group.querySelectorAll('[data-input-id]')];
    if (controls.length && controls.every((element) => Number(element.dataset.inputId.split('-')[0]) === Number(this.config.sizePosition))) {
      group.classList.add('cz-atc-size-mobile');
    }
  }

  locateNative() {
    const root = document.querySelector('main') || document;
    const candidates = [...root.querySelectorAll('product-form-component')];
    this.native = candidates.find((element) => String(element.dataset.productId) === String(this.config.productId) && !element.closest('product-card, quick-add-dialog, dialog, cz-sticky-atc'));
    this.form = this.native?.querySelector('form');
    this.nativeButton = this.native?.querySelector('[ref="addToCartButton"], button[name="add"]');
    this.nativeContainer = this.nativeButton?.closest('add-to-cart-component') || this.nativeButton;
    this.mainSection = this.native?.closest('.shopify-section');
    this.picker = [...(this.mainSection || root).querySelectorAll('variant-picker')].find((element) => String(element.dataset.productId) === String(this.config.productId) && !element.closest('product-card, quick-add-dialog, dialog, cz-sticky-atc'));
    const duo = [...(this.mainSection?.querySelectorAll('duo-selector') || [])].find((element) => String(element.dataset.productId) === String(this.config.productId));
    if (duo !== this.duo) {
      this.duoObserver?.disconnect();
      this.duo = duo;
      if (duo) {
        this.duoObserver = new MutationObserver(() => this.render());
        this.duoObserver.observe(duo, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['data-mode', 'data-ready', 'aria-busy'] });
      }
    }
  }

  activeDuo() {
    return this.duo?.isConnected && this.duo.dataset.ready === 'true' && this.duo.dataset.mode === 'duo' ? this.duo : null;
  }

  reviewDuo() {
    const duo = this.activeDuo();
    if (!duo || this.adding) return;
    this.close(false);
    duo.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    duo.querySelector('[data-duo-variant]:not(:disabled), [data-duo-add]:not(:disabled)')?.focus({ preventScroll: true });
  }

  syncGuideLink() {
    const slot = this.querySelector('[data-cz-guide-slot]');
    if (!slot) return;
    const guide = [...(this.mainSection?.querySelectorAll('cz-size-suggestion') || [])].find((element) => {
      try {
        const config = element.config || JSON.parse(element.dataset.czConfig || '{}');
        return String(config.product?.id) === String(this.config.productId);
      } catch {
        return false;
      }
    });
    const owner = guide?.closest('dialog-component.popup-link');
    const source = owner?.querySelector(':scope > button.popup-link__button');
    const dialog = owner?.querySelector('dialog[ref="dialog"]');
    if (this.guideSource !== source || this.guideOwner !== owner) {
      this.guideObserver?.disconnect();
      this.guideController?.abort();
      this.guideSource = source;
      this.guideOwner = owner;
      this.guideDialog = dialog;
      this.guideSignature = null;
      if (source && owner && dialog) {
        this.guideController = new AbortController();
        const signal = this.guideController.signal;
        owner.addEventListener('dialog:close', () => this.returnFromGuide(), { signal });
        dialog.addEventListener('close', () => this.returnFromGuide(), { signal });
        this.guideObserver = new MutationObserver(() => this.syncGuideLink());
        this.guideObserver.observe(source, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true
        });
      }
    }
    slot.hidden = !this.config.hasSize || !source?.hasAttribute('data-size-guide-link') || !dialog;
    if (slot.hidden) {
      slot.replaceChildren();
      this.guideSignature = null;
      return;
    }
    const signature = source.outerHTML;
    if (signature === this.guideSignature) return;
    const restoreFocus = slot.contains(document.activeElement);
    const mirror = source.cloneNode(true);
    for (const element of [mirror, ...mirror.querySelectorAll('*')]) {
      for (const attribute of [...element.attributes]) {
        if (attribute.name === 'id' || attribute.name === 'ref' || attribute.name.startsWith('on:') || /^on[a-z]+$/.test(attribute.name)) {
          element.removeAttribute(attribute.name);
        }
      }
    }
    mirror.type = 'button';
    mirror.dataset.czGuide = '';
    mirror.disabled = this.adding;
    mirror.setAttribute('aria-haspopup', 'dialog');
    this.guideSignature = signature;
    slot.replaceChildren(mirror);
    if (restoreFocus) mirror.focus({ preventScroll: true });
  }

  openGuide() {
    if (this.adding) return;
    this.syncGuideLink();
    if (!this.guideSource || typeof this.guideOwner?.showDialog !== 'function') {
      this.setError(this.config.strings.guideUnavailable || this.config.strings.error);
      return;
    }
    this.guideReturnPending = true;
    this.close(false);
    try {
      this.guideOwner.showDialog();
      if (!this.guideDialog?.open) throw new Error('Guide unavailable');
    } catch {
      this.guideReturnPending = false;
      this.open();
      this.setError(this.config.strings.guideUnavailable || this.config.strings.error);
    }
  }

  returnFromGuide() {
    if (!this.guideReturnPending) return;
    this.guideReturnPending = false;
    requestAnimationFrame(() => {
      if (!this.isConnected || this.dataset.ready !== 'true' || document.querySelector('dialog[open]')) return;
      this.open();
      this.querySelector('[data-cz-guide]')?.focus({ preventScroll: true });
    });
  }

  nativeOptions() {
    if (!this.picker) return [];
    return [...this.picker.querySelectorAll('input[data-input-id]:checked, select option[data-input-id]')]
      .filter((element) => element.tagName !== 'OPTION' || element.selected)
      .map((element) => ({ position: Number(element.dataset.inputId.split('-')[0]), id: String(element.dataset.optionValueId || ''), value: element.value }))
      .sort((a, b) => a.position - b.position);
  }

  matchesNative(config = this.config) {
    const native = this.nativeOptions();
    return config.options.every((option) => {
      const current = native.find((item) => item.position === Number(option.position));
      return current && (option.selectedId ? current.id === String(option.selectedId) : current.value === option.selectedValue);
    });
  }

  isMainEvent(event) {
    const picker = event.target.closest?.('variant-picker');
    return Boolean(picker && picker === this.picker);
  }

  onNativeChange(event) {
    if (!this.picker?.contains(event.target)) return;
    const target = event.target.tagName === 'SELECT' ? event.target.selectedOptions[0] : event.target;
    const position = Number(target?.dataset.inputId?.split('-')[0]);
    if (position === Number(this.config.sizePosition)) {
      this.confirmedSize = target.value;
      this.syncFailed = false;
    } else if (position) {
      this.pendingOption = { position, id: String(target.dataset.optionValueId || '') };
    }
  }

  onVariantSelected(event) {
    if (!this.isMainEvent(event)) return;
    this.fetchController?.abort();
    this.sequence += 1;
    this.pending = true;
    this.clearError();
    this.render();
    clearTimeout(this.variantTimer);
    this.variantTimer = setTimeout(() => {
      this.pending = false;
      this.syncFailed = true;
      this.setError(this.config.strings.error);
      this.render();
    }, 15000);
  }

  onVariantUpdate(event) {
    if (!this.isMainEvent(event)) return;
    if (event.detail?.data?.newProduct && String(event.detail.data.newProduct.id) !== String(this.config.productId)) {
      this.cleanup();
      return;
    }
    const next = this.readConfig(event.detail?.data?.html);
    this.locateNative();
    if (!next || !this.matchesNative(next)) return;
    clearTimeout(this.variantTimer);
    if (this.needsOptionAvailability(next)) {
      this.refresh();
      this.syncNativeVisibility();
      return;
    }
    this.pending = false;
    this.syncFailed = false;
    this.applyConfig(next);
    this.syncNativeVisibility();
  }

  readConfig(documentFragment) {
    if (!documentFragment?.querySelectorAll) return null;
    const host = [...documentFragment.querySelectorAll('cz-sticky-atc')].find((item) => item.dataset.sectionId === this.config.sectionId);
    try {
      const config = JSON.parse(host?.querySelector('[data-cz-config]')?.textContent || 'null');
      return config && String(config.productId) === String(this.config.productId) ? config : null;
    } catch {
      return null;
    }
  }

  applyConfig(next) {
    const previous = this.config;
    const otherKey = (config) => JSON.stringify(config.options.filter((option) => Number(option.position) !== Number(config.sizePosition)).map((option) => option.selectedValue));
    const changed = previous && otherKey(previous) !== otherKey(next);
    this.config = next;
    this.pendingOption = null;
    if (this.confirmedSize && !next.sizes.some((size) => size.name === this.confirmedSize && size.variant?.id && size.variant.available)) {
      this.confirmedSize = null;
      if (changed) this.setStatus(next.strings.changed);
    }
    if (next.hasSize && next.sizes.length === 1 && /^(one[\s-]?size|os|o\/s|taglia unica|unica)$/i.test(next.sizes[0].name) && next.sizes[0].variant?.available) {
      this.confirmedSize = next.sizes[0].name;
    }
    this.render();
  }

  selectedVariant() {
    if (!this.config.hasSize) return this.config.selectedVariant;
    return this.config.sizes.find((size) => size.name === this.confirmedSize)?.variant || null;
  }

  render() {
    if (!this.bar) return;
    const c = this.config;
    const duo = this.activeDuo();
    const variant = this.selectedVariant();
    const displayVariant = variant || c.selectedVariant || c.sizes.find((size) => size.variant?.id)?.variant;
    const options = c.options.filter((option) => Number(option.position) !== Number(c.sizePosition)).map((option) => `${option.name}: ${option.selectedValue}`).join(' · ');
    this.querySelector('[data-cz-bar-title]').textContent = c.title;
    this.querySelector('[data-cz-product-title]').textContent = c.title;
    this.querySelector('[data-cz-bar-options]').textContent = duo
      ? [...duo.querySelectorAll('select[data-duo-variant]')].map((select) => select.selectedOptions[0]?.textContent.trim() || '').filter(Boolean).join(' + ')
      : options;
    this.querySelector('[data-cz-options]').textContent = options;
    this.querySelector('[data-cz-change]').hidden = !options;
    this.querySelector('[data-cz-heading]').textContent = c.hasSize ? c.strings.chooseSize : c.strings.review;
    for (const price of this.querySelectorAll('[data-cz-price]')) price.textContent = displayVariant?.price || '';
    if (duo) this.bar.querySelector('[data-cz-price]').textContent = duo.querySelector('[data-duo-price]')?.textContent.trim() || '';
    for (const image of this.querySelectorAll('[data-cz-image]')) {
      const src = displayVariant?.image || c.image || '';
      if (src && image.getAttribute('src') !== src) image.src = src;
      image.hidden = !src || Boolean(duo && this.bar.contains(image));
      image.alt = `${c.title}${options ? ' — ' + options : ''}`;
    }
    this.updateOpenLabel();
    this.openButton.disabled = this.adding || Boolean(duo?.querySelector('[data-duo-add][aria-busy="true"]'));
    this.openButton.setAttribute('aria-expanded', String(this.dialog.open));
    this.querySelector('[data-cz-size-fieldset]').hidden = !c.hasSize;
    this.renderSecondaryOptions();
    this.syncGuideLink();
    const guide = this.querySelector('[data-cz-guide]');
    if (guide) guide.disabled = this.adding;
    const grid = this.querySelector('[data-cz-sizes]');
    const focusedId = grid.contains(document.activeElement) ? document.activeElement.value : null;
    grid.replaceChildren();
    for (const size of c.sizes) {
      const label = document.createElement('label');
      label.className = 'cz-atc__size';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `cz-size-${c.sectionId}`;
      input.value = size.name;
      input.dataset.czSize = size.id;
      input.checked = size.name === this.confirmedSize;
      input.disabled = !size.variant?.id || !size.variant.available || this.adding;
      input.setAttribute('aria-label', input.disabled && !this.adding ? `${size.name} — ${c.strings.soldOut}` : size.name);
      const span = document.createElement('span');
      span.textContent = size.name;
      label.append(input, span);
      grid.append(label);
      if (focusedId === size.name && !input.disabled) input.focus({ preventScroll: true });
    }
    this.submitButton.disabled = this.adding || this.pending || this.syncFailed || this.uncertain || !variant?.id || !variant.available;
    this.submitButton.textContent = this.adding ? c.strings.adding : this.pending ? c.strings.checking : variant && !variant.available ? c.strings.soldOut : c.strings.add;
    this.submitButton.setAttribute('aria-busy', String(this.adding || this.pending));
    this.querySelector('[data-cz-close]').disabled = this.adding;
    this.querySelector('[data-cz-change]').disabled = this.adding;
    this.querySelector('[data-cz-retry]').hidden = !this.syncFailed || this.adding || this.uncertain;
    this.querySelector('[data-cz-cart-link]').hidden = !this.uncertain && !this.cartError;
    if (this.pending) this.setStatus(c.strings.checking);
    else if (!this.confirmedSize && c.hasSize && !c.sizes.some((size) => size.variant?.available)) this.setStatus(c.strings.unavailable);
    else if (this.querySelector('[data-cz-status]').textContent === c.strings.checking) this.setStatus('');
    this.updateReserve();
  }

  updateOpenLabel() {
    if (!this.openButton || !this.config) return;
    const c = this.config;
    this.openButton.textContent = this.activeDuo() ? c.strings.reviewDuo : this.mobile.matches ? c.strings.add : c.hasSize ? c.strings.selectSize : c.strings.review;
  }

  renderSecondaryOptions() {
    const host = this.querySelector('[data-cz-secondary-options]');
    if (!host) return;
    const focused = host.contains(document.activeElement) ? { position: Number(document.activeElement.dataset.position), id: document.activeElement.value } : this.optionFocus;
    const expanded = new Set([...host.querySelectorAll('details[open]')].map((node) => Number(node.dataset.position)));
    const fragment = document.createDocumentFragment();
    for (const option of this.config.options) {
      if (Number(option.position) === Number(this.config.sizePosition) || !option.values?.length) continue;
      const selectedId = this.pendingOption?.position === Number(option.position) ? this.pendingOption.id : option.selectedId;
      const details = document.createElement('details');
      details.className = 'cz-atc__secondary';
      details.dataset.position = String(option.position);
      details.open = expanded.has(Number(option.position));
      const summary = document.createElement('summary');
      summary.className = 'cz-atc__summary';
      const name = document.createElement('span');
      name.textContent = option.name;
      const selection = document.createElement('span');
      selection.className = 'cz-atc__summary-value';
      selection.textContent = option.values.find((value) => value.id === selectedId)?.name || option.selectedValue;
      summary.append(name, selection);
      summary.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true"><path d="m5 8 5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'cz-atc__fieldset';
      const legend = document.createElement('legend');
      legend.className = 'cz-atc__option-legend';
      legend.textContent = option.name;
      const choices = document.createElement('div');
      choices.className = 'cz-atc__colors';
      for (const value of option.values) {
        const label = document.createElement('label');
        label.className = 'cz-atc__size';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `cz-option-${this.config.sectionId}-${option.position}`;
        input.value = value.id;
        input.dataset.czOption = '';
        input.dataset.position = String(option.position);
        input.checked = value.id === selectedId;
        const unavailable = !this.pending && !value.available;
        input.dataset.czUnavailable = String(unavailable);
        input.disabled = unavailable || this.pending || this.adding || this.uncertain;
        input.setAttribute('aria-label', value.name + (unavailable ? ` — ${this.config.strings.soldOut}` : ''));
        const text = document.createElement('span');
        text.textContent = value.name;
        label.append(input, text);
        choices.append(label);
      }
      fieldset.append(legend, choices);
      details.append(summary, fieldset);
      fragment.append(details);
    }
    host.replaceChildren(fragment);
    host.setAttribute('aria-busy', String(this.pending));
    if (focused && this.dialog.open && !this.pending) {
      const control = [...host.querySelectorAll('[data-cz-option]')].find((input) => input.value === focused.id && Number(input.dataset.position) === focused.position && !input.disabled);
      if (control) { control.focus({ preventScroll: true }); this.optionFocus = null; }
    }
  }

  onClick(event) {
    const button = event.target.closest('button, [data-cz-guide]');
    if (!button || !this.contains(button)) return;
    if (button.matches('[data-cz-open]')) this.open();
    if (button.matches('[data-cz-close]') && !this.adding) this.close();
    if (button.matches('[data-cz-change]')) this.goToPicker();
    if (button.matches('[data-cz-submit]')) this.submit();
    if (button.matches('[data-cz-retry]')) { this.clearError(); this.refresh(); }
    if (button.matches('[data-cz-guide]')) this.openGuide();
  }

  async open() {
    if (this.adding || this.dialog.open) return;
    if (this.activeDuo()) { this.reviewDuo(); return; }
    this.clearError();
    this.dialog.showModal();
    document.documentElement.classList.add('cz-atc-scroll-lock');
    this.updateVisibility();
    this.render();
    this.querySelector('[data-cz-heading]').focus({ preventScroll: true });
    if (!this.pending && !this.uncertain) await this.refresh();
    if (this.uncertain) this.setError(this.config.strings.networkError);
  }

  close(returnFocus = true) {
    this.returnFocus = returnFocus;
    this.optionFocus = null;
    if (this.dialog?.open) this.dialog.close();
    document.documentElement.classList.remove('cz-atc-scroll-lock');
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.updateVisibility();
  }

  goToPicker() {
    if (this.adding) return;
    const inlineOption = this.querySelector('[data-cz-secondary-options] details');
    if (inlineOption) {
      inlineOption.open = true;
      const control = inlineOption.querySelector('input:checked:not(:disabled), input:not(:disabled)') || inlineOption.querySelector('summary');
      control?.focus({ preventScroll: true });
      inlineOption.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      return;
    }
    this.close(false);
    const option = this.config.options.find((item) => Number(item.position) !== Number(this.config.sizePosition));
    const control = this.findOptionControl(option?.position, option?.selectedId);
    const target = control?.closest('.variant-option') || this.picker;
    target?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
    const focus = control?.tagName === 'OPTION' ? control.parentElement : control;
    focus?.focus({ preventScroll: true });
  }

  findOptionControl(position, id) {
    if (!this.picker) return null;
    return [...this.picker.querySelectorAll('[data-input-id]')].find((element) => Number(element.dataset.inputId.split('-')[0]) === Number(position) && (!id || String(element.dataset.optionValueId) === String(id)));
  }

  selectOption(position, id) {
    if (this.adding || this.pending || this.uncertain) return;
    const option = this.config.options.find((item) => Number(item.position) === position);
    const value = option?.values?.find((item) => item.id === id);
    if (!value?.available) return;
    const input = this.findOptionControl(position, id);
    if (!input) {
      this.syncFailed = true;
      this.setError(this.config.strings.error);
      this.render();
      return;
    }
    this.clearError();
    this.setStatus('');
    this.optionFocus = { position, id };
    this.syncFailed = false;
    if (input.tagName === 'OPTION') {
      input.parentElement.value = input.value;
      input.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  needsOptionAvailability(config) {
    return config.hasSize && config.options.some((option) => Number(option.position) > Number(config.sizePosition) && option.values?.some((value) => !value.available));
  }

  async resolveOptionAvailability(config, url, signal) {
    if (!this.needsOptionAvailability(config)) return;
    const affected = config.options.filter((option) => Number(option.position) > Number(config.sizePosition));
    const selectedIds = config.options.map((option) => option.selectedId);
    const sizeIndex = config.options.findIndex((option) => Number(option.position) === Number(config.sizePosition));
    const alternatives = config.sizes.filter((value) => value.id !== selectedIds[sizeIndex]);
    for (let index = 0; index < alternatives.length && this.needsOptionAvailability(config); index += 3) {
      const states = await Promise.all(alternatives.slice(index, index + 3).map(async (value) => {
        const probe = new URL(url.href);
        const ids = [...selectedIds];
        ids[sizeIndex] = value.id;
        probe.searchParams.set('option_values', ids.join(','));
        const response = await fetch(probe, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Availability unavailable');
        const state = this.readConfig(new DOMParser().parseFromString(await response.text(), 'text/html'));
        if (!state) throw new Error('Availability mismatch');
        return state;
      }));
      for (const option of affected) for (const value of option.values || []) {
        value.available ||= states.some((state) => state.options.find((item) => Number(item.position) === Number(option.position))?.values?.some((item) => item.id === value.id && item.available));
      }
    }
  }

  selectSize(name) {
    if (this.adding || this.uncertain) return;
    const size = this.config.sizes.find((item) => item.name === name);
    if (!size?.variant?.id || !size.variant.available) return;
    this.clearError();
    this.setStatus('');
    this.confirmedSize = name;
    const input = this.findOptionControl(this.config.sizePosition, size.id);
    if (!input) {
      this.syncFailed = true;
      this.setError(this.config.strings.error);
      this.render();
      return;
    }
    const sameVariant = String(this.form.elements.namedItem('id')?.value) === String(size.variant.id);
    if (sameVariant && this.matchesNative() && !this.syncFailed && !this.pending) {
      this.render();
      return;
    }
    this.syncFailed = false;
    if (input.tagName === 'OPTION') {
      input.parentElement.value = input.value;
      input.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.render();
  }

  async refresh() {
    this.fetchController?.abort();
    const controller = new AbortController();
    this.fetchController = controller;
    const sequence = ++this.sequence;
    const selected = this.nativeOptions();
    const url = new URL(this.picker?.dataset.productUrl || this.config.url, location.origin);
    if (url.origin !== location.origin) return;
    url.searchParams.delete('variant');
    url.searchParams.set('section_id', this.config.sectionId);
    if (selected.length) url.searchParams.set('option_values', selected.map((option) => option.id).join(','));
    this.pending = true;
    this.render();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Availability unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (sequence !== this.sequence || !this.isConnected) return;
      const next = this.readConfig(html);
      if (!next || !this.matchesNative(next)) throw new Error('Selection changed');
      await this.resolveOptionAvailability(next, url, controller.signal);
      if (sequence !== this.sequence || !this.isConnected) return;
      if (!this.matchesNative(next)) throw new Error('Selection changed');
      this.pending = false;
      this.syncFailed = false;
      this.applyConfig(next);
    } catch {
      if (sequence !== this.sequence || !this.isConnected) return;
      this.pending = false;
      this.syncFailed = true;
      this.setError(this.config.strings.error);
      this.render();
    } finally {
      clearTimeout(timer);
    }
  }

  async submit() {
    if (this.activeDuo()) { this.reviewDuo(); return; }
    const variant = this.selectedVariant();
    if (this.adding || this.pending || this.syncFailed || this.uncertain || !variant?.id || !variant.available) return;
    if (!this.matchesNative() || String(this.form.elements.namedItem('id')?.value) !== String(variant.id)) {
      if (this.config.hasSize) this.selectSize(this.confirmedSize);
      else this.setError(this.config.strings.error);
      return;
    }
    if (!this.form.reportValidity()) return;
    this.adding = true;
    this.clearError();
    this.setStatus('');
    this.render();
    const formData = new FormData(this.form);
    formData.set('id', String(variant.id));
    if (!formData.get('quantity')) formData.set('quantity', this.native.dataset.quantityDefault || '1');
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
    if (sections.length) formData.set('sections', sections.join(','));
    formData.set('sections_url', location.pathname + location.search);
    this.native.dataset.czSubmitting = 'true';
    const oldDisabled = this.nativeButton.disabled;
    this.nativeButton.disabled = true;
    const addController = new AbortController();
    const addTimer = setTimeout(() => addController.abort(), 20000);
    try {
      const addURL = new URL(this.config.routes.cartAdd, location.origin);
      if (!addURL.pathname.endsWith('.js')) addURL.pathname += '.js';
      const response = await fetch(addURL, { method: 'POST', signal: addController.signal, credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const data = await response.json();
      if (!response.ok || data.status) {
        const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : this.config.strings.error;
        this.cartError = true;
        this.uncertain = response.status >= 500 || (Number(data.status || response.status) === 422 && Number(formData.get('quantity')) > 1);
        this.setError(this.uncertain ? `${message} ${this.config.strings.networkError}` : message);
        window.dispatchEvent(new CartErrorEvent(this.native.id, message));
        this.adding = false;
        this.render();
        return;
      }
      this.adding = false;
      this.close(false);
      this.setStatus(this.config.strings.added);
      this.native.dispatchEvent(new CartAddEvent(data, String(variant.id), {
        source: 'product-form-component',
        productId: String(this.config.productId),
        variantId: String(variant.id),
        itemCount: Number(formData.get('quantity')) || 1,
        sections: data.sections
      }));
    } catch {
      this.uncertain = true;
      this.setError(this.config.strings.networkError);
    } finally {
      clearTimeout(addTimer);
      this.adding = false;
      this.nativeButton.disabled = oldDisabled;
      delete this.native.dataset.czSubmitting;
      this.render();
    }
  }

  setStatus(text) {
    const element = this.querySelector('[data-cz-status]');
    if (element) element.textContent = text || '';
  }

  setError(text) {
    const element = this.querySelector('[data-cz-error]');
    if (element) { element.textContent = text || ''; element.hidden = !text; }
  }

  clearError() {
    this.setError('');
  }

  updateVisibility() {
    if (!this.bar || this.dataset.ready !== 'true') return;
    const covered = [...document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]')].some((dialog) => dialog.getClientRects().length && getComputedStyle(dialog).visibility !== 'hidden');
    this.dataset.covered = String(covered);
    this.dataset.visible = String(this.mobile.matches || (this.config.settings.desktop && !this.nativeVisible));
    this.updateReserve();
  }

  updateReserve() {
    if (!this.reserve || !this.bar) return;
    const visible = this.dataset.visible === 'true';
    const height = visible ? Math.ceil(this.bar.getBoundingClientRect().height) : 0;
    this.reserve.style.height = `${height}px`;
    document.documentElement.style.setProperty('--cz-sticky-atc-height', `${height}px`);
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    this.controller?.abort();
    this.fetchController?.abort();
    this.guideObserver?.disconnect();
    this.guideController?.abort();
    this.duoObserver?.disconnect();
    this.duo = null;
    this.guideReturnPending = false;
    this.guideSource = null;
    this.guideOwner = null;
    this.optionFocus = null;
    this.pendingOption = null;
    clearTimeout(this.initTimer);
    clearTimeout(this.variantTimer);
    cancelAnimationFrame(this.coverFrame);
    this.intersection?.disconnect();
    this.resize?.disconnect();
    this.coverObserver?.disconnect();
    this.nativeContainer?.classList.remove('cz-atc-native-mobile');
    this.picker?.querySelectorAll('.cz-atc-size-mobile').forEach((element) => {
      element.classList.remove('cz-atc-size-mobile');
    });
    if (this.reserve) {
      this.reserve.remove();
      document.documentElement.style.removeProperty('--cz-sticky-atc-height');
    }
    if (this.dialog?.open) this.dialog.close();
    document.documentElement.classList.remove('cz-atc-scroll-lock');
    this.hidden = true;
    delete this.dataset.ready;
    this.controller = null;
  }
}

if (!customElements.get('cz-sticky-atc')) customElements.define('cz-sticky-atc', CZStickyATC);
